import type { GeoChartDatasource } from 'geochart';
import type { TypeFeatureInfoEntry, TypeFeatureInfoEntryPartial } from 'geoview-core/api/types/map-schema-types';
import type { ConfigBaseClass } from 'geoview-core/api/config/validation-classes/config-base-class';
import type { TypeGeochartResultSetEntry } from 'geoview-core/core/stores/states/geochart-state';
import { deepClone } from 'geoview-core/core/utils/utilities';
import type { PluginGeoChartConfig, GeoViewGeoChartConfig, GeoViewGeoChartConfigLayer } from './geochart-types';

export class GeoChartParsing {
  /**
   * Finds complete configuration necessary to build a GeoChart based on a given results set.
   *
   * @param config - The complete GeoChart Plugin configuration
   * @param layerDataArray - The Results set of results to search for a chart
   * @param onSearchIfExists - Callback function to search for a layer entry config based on a layer path
   * @returns An array of information retrieved from the results set to eventually send to the GeoChart component
   */
  static findLayerDataAndConfigFromQueryResults(
    config: PluginGeoChartConfig,
    layerDataArray: TypeGeochartResultSetEntry[],
    onSearchIfExists: SearchConfigClassDelegate
  ): [
    GeoViewGeoChartConfig | undefined,
    GeoViewGeoChartConfigLayer | undefined,
    ConfigBaseClass | undefined,
    TypeFeatureInfoEntry[] | undefined,
  ] {
    // Loop on the results set
    let foundConfigChart: GeoViewGeoChartConfig | undefined;
    let foundConfigChartLyr: GeoViewGeoChartConfigLayer | undefined;
    let foundLayerEntry: ConfigBaseClass | undefined;
    let foundData: TypeFeatureInfoEntry[] | undefined;
    layerDataArray.forEach((layerData) => {
      // If still not found data corresponding to a layer config
      if (!foundData) {
        // If found something
        if (layerData.features && layerData.features.length > 0) {
          // Find the layer config associated with the dada
          [foundConfigChart, foundConfigChartLyr] = GeoChartParsing.#findLayerConfig(config, layerData.layerPath);

          // If found a corresponding layer config
          if (foundConfigChartLyr) {
            // Grab the layer entry config associated with the layer path
            foundLayerEntry = onSearchIfExists(layerData.layerPath);

            // Grab the working data and this will exit the loop
            foundData = layerData.features;
          }
        }
      }
    });

    // Return everything as array
    return [foundConfigChart, foundConfigChartLyr, foundLayerEntry, foundData];
  }

  /**
   * Reads the configChart, configChartLayer and layerData information to determine a course of action to do to build a Datasource.
   *
   * The config might already have a Datasource attached to it, but most cases this function will fetch data from
   * somewhere to build a Datasource and attach it to the configChart.
   *
   * @param configChart - The complete GeoChart Config
   * @param configChartLayer - The layers configuration for the GeoChart Config (if existing)
   * @param layerData - The Results set of records to load in the Datasource (when already existing)
   * @returns A GeoViewGeoChartConfig with the final Datasource to send to GeoChart
   */
  static loadDatasources(
    configChart: GeoViewGeoChartConfig,
    configChartLayer: GeoViewGeoChartConfigLayer,
    layerData: TypeFeatureInfoEntryPartial[]
  ): GeoViewGeoChartConfig {
    // Clone the config to make sure we're detaching ourselves from the configuration plugin object
    const retConfigChart: GeoViewGeoChartConfig = deepClone(configChart);

    // If there's no datasources associated with the chart config, figure it out!
    if (!retConfigChart.datasources) {
      // Create the datasources
      retConfigChart.datasources = [];

      // The layer data simplified
      const layerDataSimplified = GeoChartParsing.#simplifyTypeFeatureInfoEntries(layerData);

      // If there's a query property to indicate how to query the data, we're altering the datasources
      if (configChart?.query) {
        // For each previously found data
        layerDataSimplified.forEach((lyrDataSimp) => {
          // Read the id and from the found data
          const id = lyrDataSimp[configChartLayer.propertyValue] as string;
          let display = 'Feature';
          if (configChartLayer.propertyDisplay) display = (lyrDataSimp[configChartLayer.propertyDisplay] as string) ?? display;

          // Add a datasource
          const ds: GeoChartDatasource = {
            value: id,
            display,
            sourceItem: lyrDataSimp,
          };

          // Add it to the list
          retConfigChart.datasources.push(ds);
        });
      } else {
        // Use the data as is
        // Add a datasource
        retConfigChart.datasources.push({
          display: 'Feature',
          sourceItem: layerDataSimplified[0],
          items: layerDataSimplified,
        });
      }
    } else {
      // There's already a datasource in the config, repeat it as many times as we have features to have a 'better' mockup representation of what was clicked at least
      const datasources: GeoChartDatasource[] = [];
      for (let i = 1; i <= layerData.length; i++) {
        // Add it
        datasources.push({
          ...retConfigChart.datasources[0], // spread the base datasource
          display: `Feature ${i}`, // override/add display
        });
        retConfigChart.datasources = datasources;
      }
    }

    // Return it
    return retConfigChart;
  }

  /**
   * Finds, if any, the layer configuration in the plugin configuration that's associated with the layer id given.
   *
   * When more than one could be found, the first one is returned.
   *
   * @param config - The complete GeoChart Plugin configuration
   * @param layerId - The layer id to search the layer config for
   * @returns The GeoViewGeoChartConfig and GeoViewGeoChartConfigLayer configurations
   */
  static #findLayerConfig(
    config: PluginGeoChartConfig,
    layerId: string
  ): [GeoViewGeoChartConfig | undefined, GeoViewGeoChartConfigLayer | undefined] {
    // Find the chart plugin layer config that works with layer that contains found data
    // For each chart plugin config that works with a layer config
    let foundConfigChart: GeoViewGeoChartConfig | undefined;
    let foundConfigLyr: GeoViewGeoChartConfigLayer | undefined;
    config.charts
      .filter((x: GeoViewGeoChartConfig) => {
        return x.layers;
      })
      .forEach((c: GeoViewGeoChartConfig) => {
        // If still not found
        if (!foundConfigLyr) {
          // Find the config that works with the layer (if any)
          const layerConfigs = c.layers.filter((x: GeoViewGeoChartConfigLayer) => {
            return x.layerId === layerId;
          });

          // If found any, take the first one and this will stop the loop
          if (layerConfigs.length > 0) {
            foundConfigChart = c;
            [foundConfigLyr] = layerConfigs;
          }
        }
      });

    // Return it when found
    return [foundConfigChart, foundConfigLyr];
  }

  /**
   * Simplifies the FeatureInfoEntries into more straightforward array of Record<string, unknown>.
   *
   * @param entries - The FeatureInfoEntries to simplify
   * @returns The simplified JsonObject of the attributes
   */
  static #simplifyTypeFeatureInfoEntries(entries: TypeFeatureInfoEntryPartial[]): Record<string, unknown>[] {
    // Simplify attributes
    return entries.map((entry: TypeFeatureInfoEntryPartial) => {
      // Return simplified object
      const obj: Record<string, unknown> = {};
      Object.keys(entry.fieldInfo).forEach((k: string) => {
        // Build
        obj[k] = entry.fieldInfo[k]?.value;
      });
      return obj;
    });
  }

  /**
   * Converts a Day.js date format string to a Luxon-compatible format string.
   *
   * Specifically:
   * - `YYYY` → `yyyy` (year)
   * - `DD` → `dd` (day of month), but avoids replacing `DDD` (ordinal day of year)
   *
   * @param format - The Day.js format string
   * @returns The corresponding Luxon format string
   */
  static dayjsToLuxonFormat(format: string): string {
    return format
      .replace(/YYYY/g, 'yyyy')
      .replace(/DD(?!D)/g, 'dd') // DD → dd (avoid DDD)
      .replace(/D(?!D)/g, 'd'); // D  → d  (avoid DD and DDD)
  }

  /**
   * Converts a Day.js IANA timezone string to a Luxon-compatible string.
   *
   * Currently only normalizes UTC:
   * - `'UTC'` → `'utc'`
   *
   * @param format - The Day.js timezone string
   * @returns The corresponding Luxon-compatible timezone string
   */
  static dayjsToLuxonTimeIANA(format: string): string {
    return format.replace('UTC', 'utc');
  }
}

/**
 * Type definition for the callback function used to search for a layer entry config based on a layer path.
 */
type SearchConfigClassDelegate = (layerPath: string) => ConfigBaseClass | undefined;
